import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain,
  Sparkle,
  ArrowRight,
  ArrowClockwise,
  LockKey,
  Lightning,
  TrendUp,
  Info,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/defaults";

const confidenceStyle = {
  high: "bg-emerald-100 text-emerald-700 border-emerald-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-700 border-slate-200",
};

/** Suggestion row */
const Suggestion = ({ s, i }) => (
  <div
    data-testid={`ai-suggestion-${i}`}
    className="relative rounded-xl border border-slate-200 bg-white p-4 hover:border-emerald-300 transition-colors"
  >
    <div className="flex items-start gap-3">
      <div className="shrink-0 w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200 grid place-items-center text-emerald-700 text-xs font-semibold">
        {i + 1}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm md:text-[15px] font-semibold text-slate-900 leading-snug">
            {s.action}
          </p>
          <Badge
            className={`shrink-0 rounded-md text-[10px] uppercase tracking-wide border ${
              confidenceStyle[s.confidence] || confidenceStyle.medium
            } hover:${confidenceStyle[s.confidence] || confidenceStyle.medium}`}
          >
            {s.confidence}
          </Badge>
        </div>
        <p className="mt-1.5 text-xs md:text-sm text-slate-600 leading-relaxed">
          {s.why}
        </p>
        {s.potential_gain_usd > 0 && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-700 font-semibold">
            <TrendUp size={14} weight="bold" />
            +{fmtMoney(s.potential_gain_usd)} potential
          </div>
        )}
      </div>
    </div>
  </div>
);

/** Locked overlay for users who don't have the AI Pro add-on */
const LockedOverlay = () => (
  <div
    data-testid="ai-lock-overlay"
    className="relative overflow-hidden rounded-2xl border border-slate-200"
  >
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(600px 200px at 20% 0%, rgba(16,185,129,0.10), transparent 60%), radial-gradient(600px 200px at 80% 100%, rgba(59,130,246,0.08), transparent 60%)",
      }}
    />
    <div className="relative p-6 md:p-8 bg-gradient-to-br from-white via-white to-slate-50">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-200 grid place-items-center">
          <LockKey size={22} weight="duotone" className="text-emerald-700" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-emerald-700">
              AI Pro Add-on
            </span>
            <Sparkle size={12} weight="fill" className="text-emerald-500" />
          </div>
          <h3 className="mt-1 text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
            Unlock the AI Dispatch Advisor
          </h3>
          <p className="mt-2 text-sm text-slate-600 max-w-md leading-relaxed">
            Claude Sonnet 4.6 reads this exact load and returns
            <span className="text-slate-900 font-medium"> 5 concrete moves </span>
            to make it profitable — rate targets, fuel surcharges, deadhead fixes, and hard floors.
          </p>

          <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
            <span className="rounded-full bg-white border border-slate-200 px-2.5 py-1 text-slate-600">
              +$400 avg. lift per load
            </span>
            <span className="rounded-full bg-white border border-slate-200 px-2.5 py-1 text-slate-600">
              English + Arabic
            </span>
            <span className="rounded-full bg-white border border-slate-200 px-2.5 py-1 text-slate-600">
              Bundled with Agency plan
            </span>
          </div>

          <div className="mt-5 flex items-baseline gap-2">
            <span className="tabular text-3xl md:text-4xl font-semibold text-slate-900 leading-none">
              +$30
            </span>
            <span className="text-sm text-slate-500">/ month add-on</span>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Link
              to="/app/billing"
              data-testid="ai-unlock-cta"
              className="btn-neon inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm"
            >
              <Sparkle size={14} weight="fill" />
              Unlock AI Advisor
              <ArrowRight size={14} weight="bold" className="flip-x ms-0.5" />
            </Link>
            <Link
              to="/app/billing"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm border border-slate-300 text-slate-700 hover:bg-white"
            >
              Or upgrade to Agency ($79/mo — AI included)
            </Link>
          </div>
        </div>
      </div>

      {/* Blurred fake suggestions preview */}
      <div className="mt-6 grid md:grid-cols-2 gap-3 select-none pointer-events-none">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="rounded-xl border border-slate-200 bg-white p-4 filter blur-[3px] opacity-60"
          >
            <div className="h-3 bg-slate-200 rounded w-4/5" />
            <div className="mt-2 h-2 bg-slate-100 rounded w-full" />
            <div className="mt-1 h-2 bg-slate-100 rounded w-3/4" />
            <div className="mt-3 h-3 bg-emerald-100 rounded w-1/3" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const AiAdvisorCard = ({ lang, load }) => {
  const { isAuthenticated, user } = useAuth();
  const hasAddon = !!user?.has_ai_advisor;

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/ai/advisor", { load, lang });
      setResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error("AI Advisor requires the AI Pro Add-on.");
      } else if (e?.response?.status === 401) {
        toast.error("Sign in to use the AI Advisor.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Advisor error");
      }
    } finally {
      setLoading(false);
    }
  };

  // Guests — signed out — see a slimmer prompt
  if (!isAuthenticated) {
    return (
      <div
        data-testid="ai-advisor-card"
        className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-200 grid place-items-center">
            <Brain size={22} weight="duotone" className="text-emerald-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                AI Dispatch Advisor
              </h3>
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-md text-[10px] uppercase tracking-wide">
                Pro Add-on
              </Badge>
            </div>
            <p className="mt-1.5 text-sm text-slate-600">
              Sign in to unlock Claude-powered load negotiation suggestions.
            </p>
            <Link
              to="/signup"
              data-testid="ai-signup-cta"
              className="mt-4 inline-flex items-center gap-1.5 btn-neon px-4 py-2 rounded-lg text-sm"
            >
              Sign up free
              <ArrowRight size={14} weight="bold" className="flip-x" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Authed users without the add-on → premium lock overlay
  if (!hasAddon) {
    return <LockedOverlay />;
  }

  // Full AI experience
  return (
    <div
      data-testid="ai-advisor-card"
      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
    >
      <div className="p-5 md:p-6 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-200 grid place-items-center">
            <Brain size={22} weight="duotone" className="text-emerald-700" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-slate-900 tracking-tight">
                AI Dispatch Advisor
              </h3>
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-md text-[10px] uppercase tracking-wide">
                <Sparkle size={10} weight="fill" className="me-1" />
                Pro
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Powered by Claude Sonnet 4.6 · Bilingual
            </p>
          </div>
        </div>
        <Button
          data-testid="btn-ai-run"
          onClick={run}
          disabled={loading}
          className="btn-neon rounded-lg h-9 text-xs md:text-sm"
        >
          {loading ? (
            <>
              <ArrowClockwise size={14} weight="bold" className="animate-spin me-1.5" />
              Analyzing…
            </>
          ) : result ? (
            <>
              <Lightning size={14} weight="fill" className="me-1.5" />
              Re-analyze
            </>
          ) : (
            <>
              <Lightning size={14} weight="fill" className="me-1.5" />
              Get suggestions
            </>
          )}
        </Button>
      </div>

      <div className="p-5 md:p-6">
        {loading && !result && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                data-testid={`ai-skeleton-${i}`}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="h-3 bg-slate-200 rounded w-3/4 animate-pulse" />
                <div className="mt-2 h-2 bg-slate-100 rounded w-full animate-pulse" />
                <div className="mt-1.5 h-2 bg-slate-100 rounded w-4/5 animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {!loading && !result && (
          <div
            data-testid="ai-empty"
            className="text-center py-6 text-slate-500 text-sm flex flex-col items-center gap-2"
          >
            <Info size={20} weight="duotone" className="text-emerald-600" />
            Click <span className="font-medium text-slate-700">Get suggestions</span> to analyze this load.
          </div>
        )}

        {result && (
          <div className="space-y-3">
            {result.suggestions.map((s, i) => (
              <Suggestion key={i} s={s} i={i} />
            ))}
            {result.suggestions.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">
                No suggestions returned. Try re-analyzing.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
